import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetCatalogProductsQuery,
    type CatalogProductsFilterInput,
    ProductFlow,
    ProductState,
    InfoType,
} from 'data/__generated__';
import { textColumn, booleanColumn, multiSelectColumn, entityColumn } from 'components/DataTable';
import { categorySource, supplierSource, tagSource } from 'data/entity-sources/sources';

import { formatPrice } from 'utils/price';

import { StatusTag } from 'pages/Products/components/StatusTag';
import { InfoTag } from 'pages/Products/components/InfoTag';
import { TotemImage } from 'components/TotemImage';
import { PRODUCT_FLOW_LABELS } from './flows';
import { PRODUCTS_STATES_LABELS } from './states';

export type ProductRow = GetCatalogProductsQuery['catalogProducts']['products'][number];

/**
 * Les booléens du produit, dans l'ordre où ils apparaissent en bas de la liste des colonnes. Chacun
 * donne une colonne masquée par défaut et un filtre Oui/Non ; le libellé est celui de la fiche
 * produit, raccourci à la longueur d'un en-tête de colonne.
 */
const PRODUCT_BOOLEAN_COLUMNS = [
    { key: 'isPunctual', header: 'Ponctuel' },
    { key: 'isFreefood', header: 'Freefood' },
    { key: 'isMicrostore', header: 'Microstore' },
    { key: 'isToBeArchived', header: 'À sortir' },
    { key: 'isBeingTested', header: 'En test' },
    { key: 'isPaused', header: 'En pause' },
    { key: 'isScannable', header: 'Code-barres scannable' },
    { key: 'scan', header: 'Emplacement scannable' },
    { key: 'shouldBeHiddenInStore', header: 'Caché sur l’app mobile' },
    { key: 'onboardedForSensei', header: 'Onboardé Sensei' },
    { key: 'isWithoutStock', header: 'Stock non géré' },
    { key: 'allowNegativeStock', header: 'Stock négatif autorisé' },
    { key: 'shouldOrderOnce', header: 'Commander une fois' },
    { key: 'isFromNayax', header: 'Machine à café' },
    { key: 'isDoubleCoil', header: 'Double spire' },
    { key: 'displayKg', header: 'Quantités en kg' },
    { key: 'canExpire', header: 'Peut expirer' },
    { key: 'trackExpiryDate', header: 'Monitoring des DLC' },
    { key: 'hasShortExpiryDate', header: 'DLC courte' },
    { key: 'checkSwollenLid', header: 'Vérifier l’opercule' },
] as const satisfies readonly { key: keyof ProductRow & keyof CatalogProductsFilterInput; header: string }[];

function getInfoTypeLabel(infoType: InfoType): string {
    switch (infoType) {
        case InfoType.Private:
            return 'Privé';
        case InfoType.IsToBeArchived:
            return 'À sortir';
        case InfoType.New:
            return 'Nouveau';
        case InfoType.IsInShortage:
            return 'Rupture fournisseur';
        case InfoType.IsBeingTested:
            return 'En test';
        case InfoType.Paused:
            return 'En pause';
        case InfoType.OutOfStock:
            return 'En rupture';
        default:
            return '';
    }
}

function getProductInfoTypes(product: ProductRow): InfoType[] {
    const infoTags: InfoType[] = [];

    if (product.access.length) {
        infoTags.push(InfoType.Private);
    }
    if (product.supplierShortageInfo.inShortage) {
        infoTags.push(InfoType.IsInShortage);
    }
    if (product.isToBeArchived) {
        infoTags.push(InfoType.IsToBeArchived);
    }
    if (product.isBeingTested) {
        infoTags.push(InfoType.IsBeingTested);
    }
    if (product.newUntil) {
        infoTags.push(InfoType.New);
    }
    if (product.isPaused) {
        infoTags.push(InfoType.Paused);
    }
    if (product.isOutOfStock) {
        infoTags.push(InfoType.OutOfStock);
    }

    return infoTags;
}

/**
 * Les colonnes se construisent à l'exécution : les options des filtres Catégorie et Tag viennent du
 * serveur, et celles du filtre Fournisseur sont cherchées à la frappe.
 *
 * L'ordre compte : la barre d'outils n'affiche que les DIX premiers filtres et replie le reste
 * derrière un « +N ». Les axes du quotidien passent donc devant, les booléens ferment la marche.
 */
export const productsColumns: ColumnDef<ProductRow, unknown>[] = [
    {
        id: 'image',
        accessorKey: 'imageUrl',
        header: 'Photo',
        enableSorting: false,
        meta: { defaultVisible: true },
        cell: ({ row }) => <TotemImage src={row.original.imageUrl || ''} alt={row.original.name} size={50} />,
    } as ColumnDef<ProductRow, unknown>,
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'fullname',
        accessorFn: (row) => {
            let fullName = row.name;
            if (row.brand) fullName += ` - ${row.brand}`;
            if (row.volume) fullName += ` - ${row.volume}`;
            return fullName;
        },
        header: 'Nom',
        defaultVisible: true,
    }),
    multiSelectColumn<ProductRow, CatalogProductsFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'states',
        defaultVisible: true,
        options: Object.values(ProductState).map((state) => ({
            value: state,
            label: PRODUCTS_STATES_LABELS[state] || state,
        })),
        renderOption: (option) => <StatusTag state={option.value as ProductState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    multiSelectColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'infos',
        accessorFn: (row) => getProductInfoTypes(row).join(', '),
        header: 'Info',
        filterKey: 'infos',
        defaultVisible: true,
        options: Object.values(InfoType).map((infoType) => ({
            value: infoType,
            label: getInfoTypeLabel(infoType),
        })),
        renderOption: (option) => <InfoTag infoTag={option.value as InfoType} />,
        cell: ({ row }) => {
            const infoTypes = getProductInfoTypes(row.original);
            return (
                <div className="flex flex-wrap gap-1">
                    {infoTypes.map((infoTag) => (
                        <InfoTag key={infoTag} infoTag={infoTag} />
                    ))}
                </div>
            );
        },
    }),
    entityColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'categoryTitle',
        accessorFn: (row) => row.category._id,
        header: 'Catégorie',
        filterKey: 'categories',
        defaultVisible: true,
        source: categorySource,
        cell: ({ row }) => {
            const { category, subcategory } = row.original;
            return (
                <div>
                    {category.title}
                    {subcategory ? (
                        <div className="flex flex-row">
                            {'\n'} ↳ <div>{subcategory.name}</div>
                        </div>
                    ) : null}
                </div>
            );
        },
    }),
    entityColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'tags',
        accessorFn: (row) => row.tags.map((tag) => tag.name).join(', '),
        header: 'Tag(s)',
        filterKey: 'tags',
        defaultVisible: true,
        source: tagSource,
        cell: ({ row }) => <span>{row.original.tags.map((tag) => tag.name).join(', ')}</span>,
    }),
    entityColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'supplierName',
        accessorFn: (row) => row.suppliers[0]?.name || '',
        header: 'Fournisseur #1',
        filterKey: 'supplierIds',
        defaultVisible: true,
        source: supplierSource,
        cell: ({ row }) => <span>{row.original.suppliers[0]?.name || ''}</span>,
    }),
    multiSelectColumn<ProductRow, CatalogProductsFilterInput>({
        accessorKey: 'flow',
        header: 'Flux',
        filterKey: 'flow',
        defaultVisible: false,
        options: Object.values(ProductFlow).map((flow) => ({
            value: flow,
            label: PRODUCT_FLOW_LABELS[flow] || flow,
        })),
        cell: ({ row }) => <span>{PRODUCT_FLOW_LABELS[row.original.flow] ?? row.original.flow}</span>,
    }),
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'supplierPrice',
        accessorFn: (row) => {
            const { prices = [] } = row.suppliers[0] || {};
            const { value: supplierPrice } = prices[0] || {};
            return supplierPrice !== undefined ? formatPrice(supplierPrice) : '';
        },
        header: "Prix d'achat",
        defaultVisible: true,
    }),
    ...PRODUCT_BOOLEAN_COLUMNS.map(({ key, header }) =>
        booleanColumn<ProductRow, CatalogProductsFilterInput>({
            accessorKey: key,
            header,
            filterKey: key,
        }),
    ),
];
